import { forwardRef, useImperativeHandle, useEffect, useCallback, useMemo } from "react";
import ReactGantt, { Task, Link } from "@dhx/react-gantt";
import "@dhx/react-gantt/dist/react-gantt.css";
import dayjs from "dayjs";

import { useTheme } from "../../../shared/context";
import { useGantt } from "../hooks/useGantt";
import { formatDate } from "../../../shared/utils";
import { OutageWindowData } from "../hooks/useOutageWindowData";

export interface ProjectGanttHandle {
  expandAll: () => void;
  collapseAll: () => void;
  setZoom: (level: string) => void;
  toggleSlack: (enabled: boolean) => void;
  toggleCriticalPath: (enabled: boolean) => void;
  revertTasks: (tasks: Task[], links: Link[]) => void;
}

interface ProjectGanttProps {
  tasks: Task[];
  links: Link[];
  outageWindows?: OutageWindowData[];
  isReadOnly?: boolean;
  onTaskDblClick?: (task: Task) => void;
  onAddTask?: (parentTask: Task | null) => void;
  onTaskUpdate?: (task: Task, mode: string) => void;
  onProgressUpdate?: (task: Task) => void;
  onTaskReorder?: (items: { id: number; sequence_order: number }[]) => void;
  onLinkAdd?: (link: Link) => void;
  onLinkDelete?: (linkId: number) => void;
  onTaskDragWarning?: (task: Task, mode: string, warnings: string[], title: string) => void;
}

export const ProjectGantt = forwardRef<ProjectGanttHandle, ProjectGanttProps>(
  (
    {
      tasks,
      links,
      outageWindows = [],
      isReadOnly,
      onTaskDblClick,
      onAddTask,
      onTaskUpdate,
      onProgressUpdate,
      onTaskReorder,
      onLinkAdd,
      onLinkDelete,
      onTaskDragWarning,
    },
    ref
  ) => {
    const { mode } = useTheme();

    // Create parent for outage windows group
    const outageWindowsParent = useMemo(() => {
      if (!outageWindows || outageWindows.length === 0) return null;
      return {
        id: 999000,
        text: "Outage Windows",
        type: "outage_windows_group",
        progress: 0,
        parent: 0,
        is_read_only: true,
        readonly: true,
      } as any as Task;
    }, [outageWindows]);

    // Transform outage windows into Gantt tasks
    const outageWindowTasks = useMemo(() => {
      return (outageWindows || []).map((window, idx) => ({
        id: 999001 + idx, // Use high ID range to avoid conflicts, start after parent
        text: `Outage Window`,
        start_date: dayjs(window.outage_start_date).toDate(),
        end_date: dayjs(window.outage_end_date).toDate(),
        planned_start_date: dayjs(window.outage_start_date).toDate(),
        planned_end_date: dayjs(window.outage_end_date).toDate(),
        status: window.status || "-",
        type: "outage_window",
        progress: 0,
        parent: 999000, // Parent is the outage windows group
        is_read_only: true,
        readonly: true,
      })) as Task[];
    }, [outageWindows]);

    // Combine outage windows with regular tasks (outages first)
    const allTasks = useMemo(() => {
      const result: Task[] = [];
      if (outageWindowsParent) {
        result.push(outageWindowsParent);
      }
      result.push(...outageWindowTasks, ...tasks);
      return result;
    }, [outageWindowsParent, outageWindowTasks, tasks]);
    const {
      ganttRef,
      setZoom,
      expandAll,
      collapseAll,
      handleTaskDblClick,
      handleBeforeTaskAdd,
      handleBeforeTaskDrag,
      handleAfterTaskDrag,
      handleRowDragEnd,
      // handleBeforeRowDragEnd,
      handleAfterLinkAdd,
      handleBeforeLinkDelete,
    } = useGantt({
      onTaskDblClick,
      onAddTask,
      onTaskUpdate,
      onProgressUpdate,
      onTaskReorder,
      onLinkAdd,
      onLinkDelete,
      onTaskDragWarning,
      isReadOnly,
    });

    // Patch gantt.parse to always include the 'data' key.
    // The @dhx/react-gantt useData hook can call gantt.parse({ links: [...] })
    // without a 'data'/'tasks' key when only links change, which fails validation.
    useEffect(() => {
      const ganttInstance = ganttRef.current?.instance;
      if (!ganttInstance) return;

      const originalParse = ganttInstance.parse.bind(ganttInstance);
      ganttInstance.parse = (data: any, type?: string) => {
        if (typeof data === "object" && data !== null && !data.data && !data.tasks) {
          data.data = [];
        }
        return originalParse(data, type);
      };

      // Attach onTaskDblClick directly so it fires for grid cell
      // double-clicks too (not just timeline bar double-clicks)
      if (onTaskDblClick) {
        (ganttInstance as any).attachEvent("onTaskDblClick", (id: string | number) => {
          const task = ganttInstance.getTask(id);
          if (task) {
            onTaskDblClick(task);
          }
          return false;
        });
      }
    }, [ganttRef, onTaskDblClick]);

    useEffect(() => {
      const ganttInstance = ganttRef.current?.instance;
      if (!ganttInstance) return;

      ganttInstance.clearAll();
      ganttInstance.parse({
        data: allTasks,
        links: links
      });

      // Collapse outage windows group by default
      if (outageWindowsParent) {
        ganttInstance.close(999000);
      }

    }, [allTasks, links, outageWindowsParent]);

    useEffect(() => {
      const ganttInstance = ganttRef.current?.instance;
      if (!ganttInstance) return;

      if ((ganttInstance as any).locale?.labels) {
        (ganttInstance as any).locale.labels.new_task = "New Activity";
      }

      const setTemplates = () => {
        if ((ganttInstance as any).templates) {
          (ganttInstance as any).templates.grid_row_class = (
            _start: Date,
            _end: Date,
            task: Task
          ) => {
            if (task.type === "milestone") {
              return "gantt-milestone-row";
            }
            return "";
          };

          // Custom tree icons per task type
          (ganttInstance as any).templates.grid_folder = (task: Task) => {
            if (task.type === "outage_window" || task.type === "outage_windows_group") {
              return "<div class='gantt_tree_icon gantt_icon_outage' style='color: #d32f2f;'>🚫</div>";
            }
            if (task.type === "project") {
              return "<div class='gantt_tree_icon gantt_icon_phase'></div>";
            }
            return "<div class='gantt_tree_icon gantt_icon_activity'></div>";
          };

          (ganttInstance as any).templates.grid_file = (task: Task) => {
            if (task.type === "outage_window" || task.type === "outage_windows_group") {
              return "<div class='gantt_tree_icon gantt_icon_outage' style='color: #d32f2f;'>🚫</div>";
            }
            if (task.type === "project") {
              return "<div class='gantt_tree_icon gantt_icon_phase'></div>";
            }
            if (task.type === "milestone") {
              return "<div class='gantt_tree_icon gantt_icon_milestone'></div>";
            }
            return "<div class='gantt_tree_icon gantt_icon_activity'></div>";
          };

          // Show "Unscheduled" for tasks without dates
          (ganttInstance as any).templates.task_unscheduled_time = () => {
            return "Unscheduled";
          };

          // Format date as dd/mm/yyyy, with unscheduled check
          (ganttInstance as any).templates.date_grid = (date: Date, task: any) => {
            if (task && (ganttInstance as any).isUnscheduledTask(task)) {
              return (ganttInstance as any).templates.task_unscheduled_time(task);
            }
            if (!date || !date.getFullYear) return "";
            return formatDate(date);
          };

          // Tooltip date format (dd/mm/yyyy, no time)
          (ganttInstance as any).templates.tooltip_date_format = (date: Date) => {
            if (!date || !date.getFullYear) return "";
            return formatDate(date);
          };

          (ganttInstance as any).templates.tooltip_text = (
            _start: Date,
            _end: Date,
            task: any,
          ) => {
            if (task.type === "outage_window") {
              return `
                <b>${task.text}</b>
                Start: ${task.planned_start_date ? formatDate(task.planned_start_date) : "-"}<br/>
                End: ${task.planned_end_date ? formatDate(task.planned_end_date) : "-"}<br/>
                Status: ${task.status || "-"}
              `;
            }
            return `
              <b>${task.text}</b>
              Planned Start: ${task.planned_start_date ? formatDate(task.planned_start_date) : "-"}<br/>
              Planned End: ${task.planned_end_date ? formatDate(task.planned_end_date) : "-"}<br/>
              Actual End: ${task.actual_end_date ? formatDate(task.actual_end_date) : "-"}
            `;
          };

          (ganttInstance as any).templates.task_class = (task: Task) => {
            if (task.type === "outage_window") {
              return "outage-window-task";
            }
            if (!task.planned_start_date || !task.planned_end_date) {
              return "unscheduled-task";
            }
            return "scheduled-task";
          };

          ganttInstance.render();
        }
      };

      // Use theme palette for grid row colors
      const palette = mode === "dark"
        ? { primary: { main: "#F8863F" }, secondary: { main: "#222" }, text: { primary: "#fff", secondary: "#bbb" } }
        : { primary: { main: "#E84B0F" }, secondary: { main: "#f5f5f5" }, text: { primary: "#222", secondary: "#666" } };

      const style = document.createElement("style");
      style.innerHTML = `
        .primary-grid-row .gantt_cell {
          color: ${palette.primary.main} !important;
        }

        .secondary-grid-row .gantt_cell {
          color: ${palette.secondary.main} !important;
        }

        .gantt-milestone-row .gantt_cell {
          color: ${palette.text.secondary} !important;
        }
      `;
      document.head.appendChild(style);

      setTemplates();

      const timer = setTimeout(setTemplates, 100);
      return () => clearTimeout(timer);
    }, [allTasks, ganttRef]);

    // Render slack time as visual bars on the timeline
    useEffect(() => {
      const ganttInstance = ganttRef.current?.instance as any;
      if (!ganttInstance?.addTaskLayer) return;

      ganttInstance.config.show_slack = true;

      const layerId = ganttInstance.addTaskLayer((task: Task) => {
        if (!ganttInstance.config.show_slack) {
          return null;
        }

        const slack = ganttInstance.getFreeSlack(task);
        if (!slack) {
          return null;
        }

        const state = ganttInstance.getState().drag_mode;
        if (state === "resize" || state === "move") {
          return null;
        }

        const slackStart = new Date(task.end_date as Date);
        const slackEnd = ganttInstance.calculateEndDate(slackStart, slack);
        const sizes = ganttInstance.getTaskPosition(task, slackStart, slackEnd);

        const el = document.createElement("div");
        el.className = "slack";
        el.style.left = sizes.left + "px";
        el.style.top = sizes.top + 2 + "px";
        el.style.width = sizes.width + "px";
        el.style.height = sizes.height + "px";
        return el;
      });

      return () => {
        if (ganttInstance.removeTaskLayer) {
          ganttInstance.removeTaskLayer(layerId);
        }
      };
    }, [ganttRef]);

    const toggleSlack = useCallback((enabled: boolean) => {
      const ganttInstance = ganttRef.current?.instance as any;
      if (!ganttInstance) return;
      ganttInstance.config.show_slack = enabled;
      ganttInstance.render();
    }, [ganttRef]);

    const toggleCriticalPath = useCallback((enabled: boolean) => {
      const ganttInstance = ganttRef.current?.instance as any;
      if (!ganttInstance) return;
      ganttInstance.config.highlight_critical_path = enabled;
      ganttInstance.render();
    }, [ganttRef]);

    useImperativeHandle(ref, () => ({
      expandAll,
      collapseAll,
      setZoom,
      toggleSlack,
      toggleCriticalPath,
      revertTasks: (tasks: Task[], links: Link[]) => {
        const ganttInstance = ganttRef.current?.instance as any;
        if (!ganttInstance) return;
        ganttInstance.parse({ data: tasks, links });
      },
    }));

    const ganttTheme = mode === "dark" ? "dark" : "terrace";

    return (
      <>
        <style>{`
          .gantt_add,
          .dhx_gantt_add,
          td:last-child .gantt_add,
          .gantt_grid_head_add,
          .gantt_grid_head .gantt_add,
          .gantt_grid_head_cell .gantt_add {
            display: none !important;
            visibility: hidden !important;
          }

          .gantt_task_line.gantt_critical_task {
            background-color: #e63900;
            border-color: #c53100;
          }
          .gantt_critical_task .gantt_task_progress {
            background-color: #c53100;
          }
          .gantt_critical_link .gantt_line_wrapper div {
            background-color: #e63900;
          }
          .gantt_critical_link .gantt_link_arrow {
            border-color: #e63900;
          }

          .gantt_icon_phase,
          .gantt_icon_activity,
          .gantt_icon_milestone {
            background-repeat: no-repeat;
            background-position: center center;
            background-size: 18px 18px;
          }

          .gantt_icon_phase {
            background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 24 24' fill='%23E84B0F'%3E%3Cpath d='M20 6h-8l-2-2H4c-1.1 0-1.99.9-1.99 2L2 18c0 1.1.9 2 2 2h16c1.1 0 2-.9 2-2V8c0-1.1-.9-2-2-2zm0 12H4V8h16v10z'/%3E%3C/svg%3E");
          }

          .gantt_icon_activity {
            background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 24 24' fill='%232196F3'%3E%3Cpath d='M14 2H6c-1.1 0-1.99.9-1.99 2L4 20c0 1.1.89 2 1.99 2H18c1.1 0 2-.9 2-2V8l-6-6zm2 16H8v-2h8v2zm0-4H8v-2h8v2zm-3-5V3.5L18.5 9H13z'/%3E%3C/svg%3E");
          }

          .gantt_icon_milestone {
            background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 24 24' fill='%23FF9800'%3E%3Cpath d='M12 2L2 12l10 10 10-10L12 2zm0 15.17L5.83 12 12 5.83 18.17 12 12 17.17z'/%3E%3C/svg%3E");
          }

          .gantt_icon_outage {
            display: flex;
            align-items: center;
            justify-content: center;
            font-size: 14px;
          }

          .gantt_task_line.outage-window-task {
            background: repeating-linear-gradient(
              45deg,
              #d32f2f,
              #d32f2f 10px,
              #ff6b6b 10px,
              #ff6b6b 20px
            );
            border-color: #b71c1c;
            opacity: 0.85;
            box-shadow: 0 0 0 1px #b71c1c;
          }

          .gantt_task_line.outage-window-task.gantt_task_drag {
            opacity: 0.95;
          }

          .slack {
            position: absolute;
            border-radius: 0;
            opacity: 0.7;
            border: none;
            border-right: 1px solid #b6b6b6;
            margin-left: -2px;
            background: #b6b6b6;
            background: repeating-linear-gradient(
              45deg,
              #FFFFFF,
              #FFFFFF 5px,
              #b6b6b6 5px,
              #b6b6b6 10px
            );
          }
    
          .gantt_terrace .actual-end-date {
            color: #000; 
          }
          .gantt_dark .actual-end-date {
            color: #fff;
          }
        `}</style>
        <ReactGantt
          ref={ganttRef}
          tasks={tasks}
          links={links}
          config={{
            date_format: "%d/%m/%Y",
            task_date: "%d %F %Y",
            duration_unit: "day",
            duration_step: 1,
            time_step: 60 * 24,
            scales: [
              { unit: "month", step: 1, format: "%M %Y" },
              { unit: "day", step: 1, format: "%d %M" },
            ],
            scale_height: 60,
            grid_width: 480,
            highlight_critical_path: true,
            columns: [
              { name: "text", label: "Task Name", tree: true, width: 240 },
              {
                name: "planned_start_date",
                label: "Planned Start",
                align: "center",
                width: 120,
                template: (task: any) => {
                  if (task.planned_start_date) {
                    return `<div style="color:grey;">${formatDate(task.planned_start_date)}</div>`;
                  }
                  return "-";
                },
              },
              {
                name: "planned_end_date",
                label: "Planned End",
                align: "center",
                width: 120,
                template: (task: any) => {
                  if (task.planned_end_date) {
                    return `<div style="color:grey;">${formatDate(task.planned_end_date)}</div>`;
                  }
                  return "-";
                },
              },
              {
                name: "actual_end_date",
                label: "Actual End",
                align: "center",
                width: 120,
                template: (task: any) => {
                  if (task.actual_end_date) {
                    return `<div class="actual-end-date">${formatDate(task.actual_end_date)}</div>`;
                  }
                  return "-";
                },
              }
            ],
            drag_progress: !isReadOnly,
            drag_resize: !isReadOnly,
            drag_move: !isReadOnly,
            order_branch: false,
            fit_tasks: true,
            show_empty_state: true,
          }}
          plugins={{ tooltip: true, critical_path: true }}
          theme={ganttTheme}
          onTaskDblClick={handleTaskDblClick}
          onBeforeTaskAdd={handleBeforeTaskAdd}
          onBeforeTaskDrag={handleBeforeTaskDrag}
          onAfterTaskDrag={handleAfterTaskDrag}
          onRowDragEnd={handleRowDragEnd}
          onAfterLinkAdd={handleAfterLinkAdd}
          onBeforeLinkDelete={handleBeforeLinkDelete}
          onBeforeLightbox={() => false}
        />
      </>
    );
  }
);
ProjectGantt.displayName = "ProjectGantt";
